import { useCallback, useContext, useEffect, useRef } from 'react'

import { useGlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { PointArray } from '@editor/interfaces/attribute'
import { FileProcessingStatus } from '@editor/interfaces/file'
import { InspectionArea } from '@editor/interfaces/inspection'
import { MeshBundleEntryV1, MeshBundleEntryV2Obj, MeshBundleManifest } from '@editor/interfaces/meshFiles'
import { setIsPresent, setProgress, setSolidMeshStage } from '@editor/pages/projects/common/SolidMeshManager/store'
import { getInspectionArea, getMeshBundleFiles, getMeshFileManifest } from '@editor/services/InspectionArea'
import { SignedURLCache } from '@editor/services/SignedURLCache'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { patchInspectionAreas } from '@editor/store/page'
import { MeshState, MeshTileObj, MeshTilesManager, SolidMeshManagerProps, useLODManager } from '@infraloop/common'
import { useThree } from '@react-three/fiber'
import * as Sentry from '@sentry/react'
import { useMatches } from 'react-router'
import { Vector3 } from 'three'

import useMaskRegion from '../hooks/useMaskRegion'

const SolidMeshManagerObj = ({ depthTest = true, depthWrite = true }: SolidMeshManagerProps) => {
  // Hooks
  const { camera } = useThree()

  // Route matching to check current page
  const matches = useMatches()
  const isEditorPage = matches.some((row) => row.id === 'project-editor')

  // Context
  const { getAccessToken } = useContext(UserContext)
  const { showErrorModal } = useGlobalModalContext()

  // Store
  const dispatch = useAppDispatch()
  const isAllowedToDebug = useAppSelector((state) => state.user.isAllowedToDebug)
  const project = useAppSelector((state) => state.page.project)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)

  const isMeshFileVisible = useAppSelector((state) => state.solidMesh.isVisible)
  const isMeshFilePresent = useAppSelector((state) => state.solidMesh.isPresent)

  const solidMeshTransparency = useAppSelector((state) =>
    isEditorPage ? state.editorSettings.display.mesh.opacity : state.solidMesh.transparency,
  )

  // Refs
  const tilesManagerAreaIdRef = useRef<string>('')
  const pollingAreaPromiseRef = useRef<Promise<InspectionArea> | null>(null)

  /**
   * Fetch mesh bundle signed URLs
   *
   * @return Signed URLs or undefined on error
   */
  const fetchMeshURLs = useCallback(async () => {
    if (!project?.project_id || !inspectionArea?.inspection_area_id) return null

    try {
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to get access token')

      const urls =
        (await getMeshBundleFiles(token, project.project_id, inspectionArea.inspection_area_id, showErrorModal)) ||
        undefined
      if (!urls) throw new Error('Failed to get mesh bundle files')

      return urls
    } catch (err) {
      Sentry.captureException(err)
      return null
    }
  }, [project?.project_id, inspectionArea?.inspection_area_id, getAccessToken, showErrorModal])

  // Refs
  const signedURLCache = useRef<SignedURLCache>(new SignedURLCache(fetchMeshURLs))
  const tilesManagerRef = useRef<MeshTilesManager | null>(null)
  const { forceShowOutside, maskDataTexture } = useMaskRegion(tilesManagerRef.current)
  useLODManager(tilesManagerRef.current)

  /**
   * Handle loading progress updates
   *
   * @param progress Loading progress (0 to 1)
   */
  const onLoadingProgress = useCallback(
    (progress: number) => {
      dispatch(setSolidMeshStage(progress >= 1 ? MeshState.None : MeshState.Loading))
      dispatch(setProgress(progress >= 1 ? undefined : progress))
    },
    [dispatch],
  )

  /**
   * Returns signed URL based on the path provided.
   *
   * @param path Path of the file
   */
  const urlGenerator = useCallback(async (path: string): Promise<string> => {
    try {
      return signedURLCache.current.get(path)
    } catch {
      // Fallback to the original path if the signed URL cannot be retrieved
      return path
    }
  }, [])

  /**
   * Load solid mesh file
   */
  const loadMeshFile = useCallback(
    async (projectId: string, inspectionAreaId: string, translation: PointArray) => {
      if (!tilesManagerRef.current) return

      tilesManagerRef.current.reset()
      tilesManagerAreaIdRef.current = inspectionAreaId
      dispatch(setIsPresent(true))

      try {
        const token = await getAccessToken()
        if (!token) throw new Error('Failed to get access token')

        const urls = await getMeshBundleFiles(token, projectId, inspectionAreaId, showErrorModal)
        if (!urls) throw new Error('Failed to get mesh bundle files')

        // Pre-fill cache
        signedURLCache.current.append(urls)

        // Use manifest if exists
        let manifest: MeshBundleManifest<MeshBundleEntryV2Obj> | null = null
        if (urls['manifest.json']) {
          manifest = await getMeshFileManifest<MeshBundleEntryV2Obj>(urls['manifest.json'])
          if (!manifest) throw new Error('Failed to get mesh bundle manifest')

          // Convert v1 manifest to v2 if needed
          if (manifest.version === '1.0') {
            const manifestV1 = manifest as unknown as MeshBundleManifest<MeshBundleEntryV1>
            manifest.version = '2.0'
            manifest.format = 'obj' // v1 is always obj
            manifest.meshes = manifestV1.meshes.map(({ obj_file, mtl_files }) => ({
              id: (obj_file.split('/').pop() || '').replace('.obj', ''),
              lods: [
                {
                  obj_file,
                  mtl_files: mtl_files.map(({ mtl_file, textures }) => ({
                    mtl_file,
                    textures: { material_0: textures },
                  })),
                },
              ],
            }))
          } else if (manifest.version !== '2.0' || manifest.format !== 'obj') {
            throw new Error(`Unsupported manifest: ${manifest.version}, ${manifest.format}`)
          }
        } else {
          // V0 - single mesh file
          const files = Object.entries(urls)
          const obj = files.find(([name]) => name.endsWith('.obj'))?.[1]
          const mtl = files.find(([name]) => name.endsWith('.mtl'))?.[1]
          if (!obj || !mtl || !urls['texture.jpg']) throw new Error('Failed to get .obj, .mtl or texture.jpg file')

          manifest = {
            version: '2.0',
            extracted_at: new Date().toISOString(), // as we don't have the actual extraction time, use current time
            format: 'obj', // v0 is always obj
            meshes: [
              {
                id: 'mesh-0',
                lods: [
                  {
                    obj_file: 'mesh.obj',
                    mtl_files: [
                      {
                        mtl_file: 'mesh.mtl',
                        textures: {
                          material_0: {
                            map_kd: 'texture.jpg',
                          },
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          }
        }

        if (!manifest) throw new Error('Mesh bundle manifest is missing')

        // Combine all mesh splits into a single container
        tilesManagerRef.current.clear() // Clear any existing meshes
        tilesManagerRef.current.name = `solid-mesh-manager`
        tilesManagerRef.current.userData = {
          projectId,
          inspectionAreaId,
        }
        tilesManagerRef.current.onLoadingProgress = onLoadingProgress
        tilesManagerRef.current.camera = camera

        // Add each tile mesh LODs
        manifest.meshes.forEach((mesh) => {
          const tile = new MeshTileObj(mesh)
          tile.depthTest = depthTest
          tile.depthWrite = depthWrite
          tile.maskDataTexture = maskDataTexture
          tile.opacity = solidMeshTransparency
          tile.setURLGenerator(urlGenerator)
          tilesManagerRef.current!.add(tile)

          // DEBUG
          if (isAllowedToDebug) {
            tile.debug()
          }
          // END DEBUG
        })

        // If offset is defined in manifest, apply it, prioritizing manifest over offset.xyz file
        const xyzFile = Object.entries(urls).find(([file]) => file.endsWith('offset.xyz'))?.[1]
        if (manifest.offset) {
          tilesManagerRef.current.setOffsetPosition(
            new Vector3().fromArray(manifest.offset).sub(new Vector3().fromArray(translation)),
          )
        } else if (xyzFile) {
          // Load xyz file for positioning
          await fetch(xyzFile).then(async (res) => {
            if (!res.ok) throw new Error('Failed to load xyz file')

            const pos = (await res.text()).split(' ').map((v) => parseFloat(v)) as PointArray
            tilesManagerRef.current?.setOffsetPosition(
              new Vector3().fromArray(pos).sub(new Vector3().fromArray(translation)),
            )

            return pos
          })
        } else {
          // Even if no xyz file, we still need to apply inspection area translation
          tilesManagerRef.current.setOffsetPosition(
            tilesManagerRef.current?.position.sub(new Vector3().fromArray(translation)),
          )
        }

        await tilesManagerRef.current.preload()
      } catch (err) {
        Sentry.captureException(err)
      }
    },
    [
      isAllowedToDebug,
      camera,
      depthTest,
      depthWrite,
      maskDataTexture,
      solidMeshTransparency,
      urlGenerator,
      onLoadingProgress,
      getAccessToken,
      showErrorModal,
      dispatch,
    ],
  )

  /**
   * Poll inspection area mesh status until it's ready.
   *
   * @param projectId ID of the project
   * @param areaId ID of the inspection area
   * @param meshUploadedAt Timestamp of when the mesh was uploaded by user (not the one recorded in inspection area)
   */
  const pollMeshStatus = useCallback(
    async (projectId: string, areaId: string, meshUploadedAt?: number): Promise<InspectionArea | null> => {
      // Prevent multiple polling for the same area
      if (pollingAreaPromiseRef.current) return pollingAreaPromiseRef.current

      const interval = 3000
      const intervalCap = 6000
      const timeout = 60000 * 15 // 15 minutes
      const startTime = Date.now()
      let retries = 0
      pollingAreaPromiseRef.current = new Promise<InspectionArea>((resolve, reject) => {
        const check = async () => {
          // Timeout
          if (Date.now() - startTime > timeout) {
            reject(new Error('Polling inspection area mesh status timed out'))
            return
          }

          const token = await getAccessToken()
          if (!token) {
            reject(new Error('Failed to get access token'))
            return
          }

          const ar = await getInspectionArea(token, projectId, areaId)
          if (!ar) {
            reject(new Error('Failed to get inspection area'))
            return
          }

          // If it fails, show warning to retry
          if (ar.mesh_bundle_status?.status === FileProcessingStatus.FAILED) {
            showErrorModal(
              'メッシュファイルの処理に失敗しました。ファイルを再アップロードして、もう一度お試しください。',
            )
            dispatch(setSolidMeshStage(MeshState.None))
            reject(new Error('Inspection area mesh processing failed'))
            return
          }

          // if the mesh bundle status is still not set or it still holds the old mesh bundle status, keep retrying
          if (
            !ar.mesh_bundle_status ||
            ar.mesh_bundle_status.status === FileProcessingStatus.RUNNING ||
            (meshUploadedAt && ar.mesh_bundle_status.uploaded_at <= meshUploadedAt)
          ) {
            retries += 1
            setTimeout(
              () => {
                void check()
              },
              retries <= 3 ? interval : Math.min(interval * retries ** 2, intervalCap),
            )
            return
          }

          dispatch(patchInspectionAreas([ar]))
          resolve(ar)
        }

        setTimeout(() => check(), interval)
      })

      try {
        const ar = await pollingAreaPromiseRef.current
        await loadMeshFile(projectId, ar.inspection_area_id, ar.translation)
        pollingAreaPromiseRef.current = null
        return ar
      } catch (error) {
        Sentry.captureException(error)
        return Promise.reject(error instanceof Error ? error : new Error(String(error)))
      }
    },
    [getAccessToken, showErrorModal, dispatch, loadMeshFile],
  )

  /**
   * Update solid mesh transparency when settings change
   */
  useEffect(() => {
    tilesManagerRef.current?.setOpacity(forceShowOutside ? 0.1 : solidMeshTransparency)
  }, [solidMeshTransparency, forceShowOutside])

  /**
   * Load solid mesh when inspection area or project changes
   */
  useEffect(() => {
    if (
      !project?.project_id ||
      !inspectionArea?.inspection_area_id ||
      tilesManagerAreaIdRef.current === inspectionArea?.inspection_area_id
    )
      return

    // Check status if mesh processing is still ongoing
    if (inspectionArea?.mesh_bundle_status?.status === FileProcessingStatus.RUNNING) {
      dispatch(setIsPresent(false))
      dispatch(setSolidMeshStage(MeshState.Processing))
      void pollMeshStatus(project.project_id, inspectionArea.inspection_area_id)
      return
    }

    // Mesh file is not ready for use
    if (inspectionArea?.mesh_bundle_status?.status !== FileProcessingStatus.SUCCEEDED) {
      dispatch(setIsPresent(false))
      return
    }

    void loadMeshFile(project.project_id, inspectionArea.inspection_area_id, inspectionArea.translation)
  }, [
    project?.project_id,
    inspectionArea?.inspection_area_id,
    inspectionArea?.mesh_bundle_status,
    inspectionArea?.translation,
    loadMeshFile,
    pollMeshStatus,
    dispatch,
  ])

  /**
   * Update solid mesh when inspection area is updated.
   */
  useEffect(() => {
    const onAreaUpdated = ({ detail }: CustomEventInit<{ area: InspectionArea; meshUploadedAt?: number }>) => {
      if (!detail?.area || !project?.project_id || !tilesManagerRef.current) return
      const { area, meshUploadedAt } = detail

      // if there's a file, but the updated area does not have a mesh bundle status,
      // it means that the mesh was deleted, so we reset the init flag and PCDs to force reload canvas
      if (isMeshFilePresent && !area.mesh_bundle_status?.uploaded_at) {
        dispatch(patchInspectionAreas([area]))
        dispatch(setIsPresent(false))
        tilesManagerRef.current.reset()
      }
      // if the event is marked with mesh uploaded timestamp but area does not have a mesh bundle status,
      // it means that the mesh was uploaded, but not processed yet, so we force loop fetch inspection area
      // to get the mesh bundle status
      else if (meshUploadedAt) {
        tilesManagerRef.current.reset()
        dispatch(setIsPresent(false))
        dispatch(setSolidMeshStage(MeshState.Processing))

        void pollMeshStatus(project.project_id, area.inspection_area_id, meshUploadedAt)
      }
    }

    document.addEventListener('inspection-area:updated', onAreaUpdated)

    return () => {
      document.removeEventListener('inspection-area:updated', onAreaUpdated)
    }
  }, [project?.project_id, isMeshFilePresent, pollMeshStatus, dispatch])

  return <meshTilesManager visible={isMeshFileVisible && solidMeshTransparency > 0} ref={tilesManagerRef} />
}

export default SolidMeshManagerObj
